import React from 'react';
import { colors } from '../../styles/tokens';
import type { MetaCobertura } from '../../types/indicadores.types';
import { diaCorto } from '../../utils/meta';

export interface AvisoCoberturaProps {
  cobertura: MetaCobertura;
}

function listar(fechas: string[], tope = 6): string {
  const vistos = fechas.slice(0, tope).map(diaCorto).join(', ');
  return fechas.length > tope ? `${vistos} y ${fechas.length - tope} más` : vistos;
}

/**
 * Banda de aviso cuando el denominador no está completo.
 *
 * Existe porque el modo de falla de este tablero no es equivocarse: es acertar
 * a medias sin decirlo. Un 91 % calculado sobre 150 de 157 tiendas parece una
 * respuesta, y nadie va a dudar de un número con dos decimales. Cuando falta
 * algo, el porcentaje viene `null` desde la API y esta banda dice qué falta.
 *
 * Los días sin venta materializada NO son un error: son los días que el
 * snapshot nocturno todavía no ha cerrado, típicamente hoy. Se dicen aparte,
 * en tono neutro, para no confundir «aún no ha pasado» con «se perdió».
 */
const AvisoCobertura: React.FC<AvisoCoberturaProps> = ({ cobertura }) => {
  const { completa, diasSinMeta, diasCortos, tiendasEsperadas, diasSinVenta } = cobertura;

  if (completa && diasSinVenta.length === 0) return null;

  return (
    <div
      role="status"
      data-testid="aviso-cobertura"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        background: completa ? '#f4f6fa' : '#fff6ed',
        border: `1px solid ${completa ? colors.border : '#f0d3b4'}`,
        borderRadius: 14,
        padding: '12px 16px',
        fontSize: 13,
        color: colors.texto.body,
        lineHeight: 1.45,
      }}
    >
      {diasSinMeta.length > 0 && (
        <span>
          <strong style={{ color: colors.naranja }}>Sin meta cargada</strong> en{' '}
          {diasSinMeta.length} {diasSinMeta.length === 1 ? 'día' : 'días'} del periodo (
          {listar(diasSinMeta)}). El cumplimiento no se muestra: se estaría midiendo contra un
          presupuesto incompleto.
        </span>
      )}

      {diasCortos.length > 0 && (
        <span>
          <strong style={{ color: colors.naranja }}>Metas incompletas</strong> en{' '}
          {diasCortos.length} {diasCortos.length === 1 ? 'día' : 'días'}:{' '}
          {diasCortos
            .slice(0, 4)
            .map((d) => `${diaCorto(d.fecha)} (${d.tiendas} de ${tiendasEsperadas} tiendas)`)
            .join(', ')}
          {diasCortos.length > 4 && ` y ${diasCortos.length - 4} más`}.
        </span>
      )}

      {diasSinVenta.length > 0 && (
        <span style={{ color: colors.texto.tertiary }}>
          La curva se corta en {listar(diasSinVenta)}: son días que el cierre nocturno todavía no
          ha materializado. No es venta perdida.
        </span>
      )}
    </div>
  );
};

export default AvisoCobertura;
